import React from 'react';
import { ApolloError, useLazyQuery } from 'apollo-client-ppc';
import debounce from 'lodash/debounce';
import { AsyncSelect } from '@atlaskit/select';
import { fetchCompaniesInPPC } from './queries/fetch-company-names';
import { generatedTypesNew } from '@css-xp/graphtypes';

export const DEBOUNCE_WAIT_TIME = 1000;

type SearchCompanyProps = {
  transactionAccountId: string;
  placeholder?: string;
  onSelectCompany: (selectedCompany: string) => void;
  onFetchError?: (error: ApolloError) => void;
};

export const SearchCompany: React.FC<SearchCompanyProps> = ({
  transactionAccountId,
  placeholder = `Start typing the customer's name`,
  onSelectCompany,
  onFetchError,
}) => {
  const [fetchCompanyNames, { loading }] = useLazyQuery<
    generatedTypesNew.FetchCompaniesInPpcQuery,
    generatedTypesNew.FetchCompaniesInPpcQueryVariables
  >(fetchCompaniesInPPC, {
    variables: { searchParams: { query: '', transactionAccountId } },
  });

  const loadCompanyNameOptions = async (term: string) => {
    if (term) {
      const { data, error } = await fetchCompanyNames({
        variables: {
          searchParams: { query: term, transactionAccountId },
        },
      });

      if (error) {
        onFetchError && onFetchError(error);
      }

      const results = data?.shipToParty?.nodes ?? [];
      if (results.length === 0) {
        return [];
      }
      const companyNames = [...new Set(results.map((result) => result?.name).filter(Boolean))] as string[];
      const companies = companyNames.map((companyName) => ({
        label: companyName,
        value: companyName,
      }));
      return companies;
    }
  };

  const loadOptions = React.useCallback(
    debounce((inputText, callback) => {
      loadCompanyNameOptions(inputText).then((options) => callback(options));
    }, DEBOUNCE_WAIT_TIME),
    []
  );

  return (
    <div data-testid="ppc-search-companies-container">
      <AsyncSelect
        name="select-company-name"
        isOpen={true}
        placeholder={placeholder}
        isLoading={loading}
        loadOptions={loadOptions}
        isSearchable={true}
        isClearable={true}
        onChange={(selectedCompany) => {
          const company = (selectedCompany as unknown) as any;
          onSelectCompany(company);
        }}
      />
    </div>
  );
};
